import { Component, ElementRef, Input, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DynamicConfirmModalComponent } from '../../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';

import { FileCheckerService } from '../../../../shared/documents/file-checker.service';
import { BaseLearningSectionComponent } from '../../../../shared/learning-section/base-learning-section/base-learning-section.component';
import { TimelineSectionStatus } from '../../../../_models/timeline-models/timeline-section-status.enum';
import { LearningSectionFlowControlHelperService } from '../../../../_helpers/learning-section-flow-control-helper/learning-section-flow-control-helper.service';
import { DocumentSection } from '../../../../_models/timeline-models/section-models/document-section.model';
import { SuccessModalHelperService } from '../../../../_helpers/success-modal-helper/success-modal-helper.service';
import { TimelineSectionsService } from '../../../../_services/timeline-sections/timeline-sections.service';
import { ErrorModalHelperService } from '../../../../_helpers/error-modal-helper/error-modal-helper.service';
import { FeatureHelperService } from '../../../../_helpers/feature-helper/feature-helper.service';
import { LeapV2EvidenceService } from '../../../tutor-report-flows/tutor-report-leap-v2/services/leap-v2-evidence.service';

@Component({
    selector: 'document-section',
    templateUrl: './document-section.component.html',
    styleUrls: ['./document-section.component.css'],
    standalone: false
})
export class DocumentSectionComponent extends BaseLearningSectionComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;
  @Input() timelineSection: DocumentSection = new DocumentSection();
  fileAttr = '';
  fileAttrHolder = '';

  public enumHolder = TimelineSectionStatus;

  public viewDocument: boolean = false;

  /**
   * The document as a local blob: URL for the preview. The card's documentUrl is the API's link
   * (api/Files/Document/{id}), which needs the signed-in user's headers, so it is fetched through the API, never
   * opened from storage (bug 40444).
   */
  public documentPreviewUrl: string = null;

  constructor(private readonly fileCheckerService: FileCheckerService,
    protected readonly flowHelperService: LearningSectionFlowControlHelperService,
    public dialog: MatDialog,
    private successModalHelper: SuccessModalHelperService,
    private timelineSectionService: TimelineSectionsService,
    private errorModalHelper: ErrorModalHelperService,
    protected featureHelperService: FeatureHelperService,
    private evidenceService: LeapV2EvidenceService
  ) {
    super(flowHelperService, dialog);
    this.sectionTitle = 'Document';
  }

  ngOnInit() {
    if (this.timelineSection.status == null) {
      this.timelineSection.status = TimelineSectionStatus.Saved;
    }
    if (this.editMode) {
      this.loadPreview();
    }
  }

  ngOnDestroy(): void {
    this.releasePreview();
  }

  /** Shows or hides the document, fetching it the first time. */
  public toggleDocument(): void {
    this.viewDocument = !this.viewDocument;
    if (this.viewDocument) {
      this.loadPreview();
    }
  }

  /** Fetches the document through the API; only the API's own link is followed. */
  private loadPreview(): void {
    const url = this.timelineSection?.documentUrl;
    if (this.documentPreviewUrl || !url || !url.startsWith('api/')) return;
    this.evidenceService.download({ name: this.timelineSection.documentTitle, url }).subscribe(blob => {
      this.releasePreview();
      this.documentPreviewUrl = URL.createObjectURL(blob);
    });
  }

  private releasePreview(): void {
    if (this.documentPreviewUrl) {
      URL.revokeObjectURL(this.documentPreviewUrl);
      this.documentPreviewUrl = null;
    }
  }

  public uploadFileEvt(imgFile: any): void {
    if (imgFile.target.files && imgFile.target.files[0]) {
      if (imgFile.target.files[0].size == 0) {
        this.fileInput.nativeElement.value = "";
        this.fileAttr = this.fileAttrHolder;
        this.timelineSection.documentBase64Path = "";
      } else if (this.fileCheckerService.isAcceptedFileMimeType(imgFile.target.files[0].type)) {
        this.fileAttr = '';
        this.fileAttr += imgFile.target.files[0].name;
        this.timelineSection.documentExtension = imgFile.target.files[0].type;
        
        // HTML5 FileReader API
        let reader = new FileReader();
        reader.onload = (e: any) => {
          this.timelineSection.documentBase64Path = e.target.result;
        };
        reader.readAsDataURL(imgFile.target.files[0]);
        
        // Reset if duplicate image uploaded again
        this.fileInput.nativeElement.value = "";
      } else {
        this.fileInput.nativeElement.value = "";
        this.fileAttr = this.fileAttrHolder;
        this.timelineSection.documentBase64Path = "";
        this.timelineSection.documentExtension = "";
        //this.showInvalidFileTypeError = true;
      }

    } else {
      this.fileAttr = this.fileAttrHolder;
      //this.showInvalidFileTypeError = false;
    }
  }

  public showMarkForDeletionButton(): boolean {
    return (this.timelineSection.status === TimelineSectionStatus.New ||
      this.timelineSection.status === TimelineSectionStatus.Saved ||
      this.timelineSection.status === TimelineSectionStatus.Submitted ||
      this.timelineSection.status === TimelineSectionStatus.Rejected) &&
      this.timelineSection.documentUrl != null;
  }

  validate(): boolean {
    let sectionDate = new Date(this.timelineSection.sectionDate)
    sectionDate.setHours(0, 0, 0, 0);
    if (sectionDate < this.minSectionDate) {
      return false;
    } else if (!this.timelineSection.documentUrl && (!this.timelineSection.documentBase64Path || this.timelineSection.documentBase64Path == '')) {
      this.errorModalHelper.show("Please select a document to upload.", { isValidationError: true });
      return false;
    } else {
      return true;
    }

  }
 
  public save(dialogRef: MatDialogRef<any>): void {
    if (this.validate()) {
      if (this.timelineSection.deleteDocument) {
        let confirmRef = this.dialog.open(DynamicConfirmModalComponent, {
          data: { confirmMessage: "If you save this section and no document is attached then it will permanently deleted, are you sure you wish to continue?" },
          panelClass: 'my-confirm-dialog'
        });
        confirmRef.afterClosed().subscribe((confirm: boolean) => {
          if (confirm) {
            let oldStatus = this.timelineSection.status;
            this.timelineSection.status = TimelineSectionStatus.Saved;          
            this.postMerge(oldStatus);
          } 
        });
      } else {
        let oldStatus = this.timelineSection.status;
        this.timelineSection.status = TimelineSectionStatus.Saved;      
        this.postMerge(oldStatus);
      }
    }
  }
  
  public submit(dialogRef: MatDialogRef<any>): void {
    if (this.validate()) {
      if (this.timelineSection.deleteDocument) {
        let confirmRef = this.dialog.open(DynamicConfirmModalComponent, {
          data: { confirmMessage: "If you submit this section and no document is attached then it will permanently deleted, are you sure you wish to continue?" },
          panelClass: 'my-confirm-dialog'
        });
        confirmRef.afterClosed().subscribe((confirm: boolean) => {
          if (confirm) {
            let oldStatus = this.timelineSection.status;
            this.timelineSection.status = TimelineSectionStatus.Submitted;
            this.postMerge(oldStatus);
          } 
        });
      } else {
        let oldStatus = this.timelineSection.status;
        this.timelineSection.status = TimelineSectionStatus.Submitted;      
        this.postMerge(oldStatus);
      }
    }
  }
  
  public unlock(dialogRef: MatDialogRef<any>): void {
    let oldStatus = this.timelineSection.status;
    this.timelineSection.status = TimelineSectionStatus.Unlock;
    this.postMerge(oldStatus);
  }
  
  public approve(dialogRef: MatDialogRef<any>): void {
    let oldStatus = this.timelineSection.status;
    this.timelineSection.status = TimelineSectionStatus.Approved;
    this.postMerge(oldStatus);
  }
  
  public reject(dialogRef: MatDialogRef<any>): void {
    let oldStatus = this.timelineSection.status;
    this.timelineSection.status = TimelineSectionStatus.Rejected;
    this.postMerge(oldStatus);
  }

  public isDocumentFormEditable(): boolean {
    if (!this.timelineSection) {
      return false;
    }
    return (this.timelineSection.status === TimelineSectionStatus.New ||
      this.timelineSection.status === TimelineSectionStatus.Saved ||
      this.timelineSection.status === TimelineSectionStatus.Rejected) &&
      this.editMode;
  }

  private postMerge(oldStatus: TimelineSectionStatus): void {
      this.timelineSectionService.mergeDocumentSection(this.placementId, this.students.map(s => s.studentId), this.subject, this.timelineSection).subscribe((response: DocumentSection) => {
        this.timelineSection = response;
        this._sectionStatusUpdated.next(response.status);
        this.successModalHelper.show(this.timelineSection.status + " successfully");
      }, error => {
        console.error(error);
        this.errorModalHelper.show(error.message, error);
        this.timelineSection.status = oldStatus;
      });
    
  }

}
